import './EditLinkModal.css';

import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { TrackLinkDelete, TrackLinkEdit } from 'utilities/insights';
import { deleteLink, editLink, getLink } from 'redux/thunks/dashboard';
import { resetDeletingLink, resetEditingLink, resetGettingLink } from 'redux/actions/dashboard';
import { useDispatch, useSelector } from 'react-redux';

import AppLoader from 'components/Loader';
import Modal from 'components/ModalWrapper';
import { sortAndRemoveDuplicateStringValues } from 'utilities/arraySort';
import swal from 'sweetalert2';
import { validURL } from 'utilities/Validations';

const EditLinkModal = (props) => {
    const { show, siteId, dashboardId, linkId, linkCategories, onClose } = props;
    const [category, set_category] = useState('');
    const [URL, set_URL] = useState('');
    const [description, set_description] = useState('');
    const dashboard = useSelector((state) => state.dashboard);

    const [fieldErrors, setFieldErrors] = useState({});
    const categories = useSelector((state) => state.site.categories);
    const dispatch = useDispatch();
    const filter = createFilterOptions();

    const handleDelete = async () => {
        const result = await swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it.',
        });

        if (result.value) {
            TrackLinkDelete(siteId, dashboardId, category);
            dispatch(deleteLink(siteId, dashboardId, linkId));
            handleClose();
        }
    };

    const validate = () => {
        let errors = {};

        if (!URL) {
            errors['url'] = 'This field is required';
        } else {
            const validUrl = validURL(URL);

            if (!validUrl) {
                errors['url'] = 'Please enter a valid url';
            }
        }

        if (!description) {
            errors['description'] = '';
        }

        if (!category) {
            errors['category'] = '';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        let linkToUpdate = {
            siteId,
            dashboardId,
            linkId,
            category: category,
            description: description,
            URL: URL,
        };

        if (!validate()) {
            return;
        }
        
        TrackLinkEdit(siteId, dashboardId, category);
        dispatch(editLink(linkToUpdate));
    };

    const handleClose = useCallback(() => {
        dispatch(resetGettingLink());
        onClose();
    }, [dispatch, onClose]);

    useEffect(() => {
        if (show) {
            dispatch(getLink(siteId, dashboardId, linkId));
        }
    }, [show, siteId, dashboardId, linkId, dispatch]);

    useEffect(() => {
        if (dashboard.gettingLinkSuccess) {
            set_URL(dashboard.link.URL);
            set_description(dashboard.link.description);
            set_category(dashboard.link.category);
        }
    }, [dashboard.gettingLinkSuccess, dashboard.link]);

    useEffect(() => {
        if (dashboard.editingLinkSuccess) {
            handleClose();
            dispatch(resetEditingLink());
        }
    }, [dashboard.editingLinkSuccess, dispatch, handleClose]);

    useEffect(() => {
        if (dashboard.deletingLinkSuccess) {
            handleClose();
            dispatch(resetDeletingLink());
        }
    }, [dashboard.deletingLinkSuccess, handleClose, dispatch]);

    return (
        <Modal allowClickOutsideToClose height='small' open={show} onCloseClicked={handleClose}>
            <Grid container direction='column' className='add-info-modal'>
                <Grid container item justifyContent='center' className='add-info-modal__title'>
                    <Typography variant='h5'>Edit Link</Typography>
                </Grid>

                <Grid container direction='column' className='add-info-modal__body'>
                    {dashboard.editingLink || dashboard.deletingLink || dashboard.gettingLink || !URL ? (
                        <AppLoader />
                    ) : (
                        <>
                            <TextField
                                error={'url' in fieldErrors}
                                helperText={'url' in fieldErrors && fieldErrors['url']}
                                variant='outlined'
                                label='Url *'
                                placeholder='Enter your new link here...'
                                value={URL}
                                onChange={(e) => set_URL(e.target.value)}
                                className='edit-link-modal__form-field'
                            />
                            <TextField
                                error={'description' in fieldErrors}
                                helperText={'description' in fieldErrors && 'This field is required'}
                                className='edit-link-modal__form-field'
                                variant='outlined'
                                label='Description *'
                                placeholder='Write a short description...'
                                value={description}
                                onChange={(e) => set_description(e.target.value)}
                                maxLength='100'
                            />
                            <Autocomplete
                                id="category-dropdown"
                                className='add-link-modal__form-field'
                                value={category}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);

                                    // Suggest the creation of a new value
                                    if (params.inputValue !== '') {
                                        filtered.push(params.inputValue);
                                        set_category(params.inputValue);
                                    }
                                    return filtered;
                                }}
                                selectOnFocus
                                options={sortAndRemoveDuplicateStringValues([...categories, ...linkCategories])}
                                getOptionLabel={(option) => {
                                    // Value selected with enter, right from the input
                                    if (typeof option === 'string') {
                                        return option;
                                    }
                                    // Add "xxx" option created dynamically
                                    if (option.inputValue) {
                                        return option.inputValue;
                                    }
                                    // Regular option
                                    return option;
                                }}
                                onInputChange={(event, value, reason) => {set_category(value)}}
                                renderOption={(option) =>  {return option}}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField {...params} label="Category *" placeholder='Add a category or select an existing one...' variant="outlined" />
                                )}
                            />
                        </>
                    )}
                </Grid>

                <Grid container justifyContent='space-between' className='edit-link-modal__footer'>
                    <div className='edit-link-modal__footer-buttons--left'>
                        <Button
                            color='default'
                            size='large'
                            variant='contained'
                            onClick={handleClose}
                            className='edit-link-modal__footer-button--close'
                        >
                            Cancel
                        </Button>
                        <Button
                            color='secondary'
                            size='large'
                            variant='contained'
                            onClick={() => {
                                handleDelete();
                            }}
                            disabled={dashboard.editingLink || dashboard.deletingLink || dashboard.gettingLink || !URL}
                            className='edit-link-modal__footer-button--delete'
                        >
                            Delete
                        </Button>
                    </div>
                    <Button
                        color='primary'
                        size='large'
                        variant='contained'
                        disabled={dashboard.editingLink || dashboard.deletingLink || dashboard.gettingLink || !URL}
                        onClick={() => {
                            handleSubmit();
                        }}
                        className='edit-link-modal__footer-button--save'
                    >
                        Save Changes
                    </Button>
                </Grid>
            </Grid>
        </Modal>
    );
};

export default EditLinkModal;
